/** 'indeterminate' when the saved rows disagree, so each keeps its own mark. */
export type CheckedState = boolean | 'indeterminate';

/** Ticked only when every saved row is checked, so a prefilled form starts unticked. */
export function savedCheckedState(rows?: { checked: boolean }[]): CheckedState {
    const marks = (rows ?? []).map((row) => row.checked);
    if (marks.length === 0 || marks.every((mark) => !mark)) return false;
    return marks.every(Boolean) ? true : 'indeterminate';
}

/** Stamps the box on every row; a half-ticked box keeps each row's saved mark, matched by `keyOf`. */
export function applyCheckedMark<
    T extends { checked: boolean },
    S extends { checked: boolean },
>(
    payload: T[],
    state: CheckedState,
    saved: S[],
    keyOf: (row: T | S) => string,
): T[] {
    if (state !== 'indeterminate') {
        return payload.map((row) => ({ ...row, checked: state }));
    }
    const savedMarks = new Map(saved.map((row) => [keyOf(row), row.checked]));
    return payload.map((row) => ({
        ...row,
        checked: savedMarks.get(keyOf(row)) ?? false,
    }));
}

/** Keeps Claude's unsure flag on a row that is still unchecked and matches a saved unsure row. */
export function applyUnsureMark<T extends { checked: boolean }>(
    payload: T[],
    unsureKeys: ReadonlySet<string>,
    keyOf: (row: T) => string,
): (T & { unsure: boolean })[] {
    return payload.map((row) => ({
        ...row,
        unsure: !row.checked && unsureKeys.has(keyOf(row)),
    }));
}
